import { workedVisitsCard } from "./worked-visits";
import type { ScreenContext } from "../../shell/types";
import { api, el } from "../../ui/dom";
import { card, chip, table } from "../../ui/record";

// The patient's Billing and payroll tab (brief 1E-1, mockup billingTab): the patient's worked
// visits (brief 1E-33, the one list shared with Billing, Payroll and the caregiver), the
// patient's invoices, and the episode's margin to date (the Money switch).
// Reading needs Billing at View; without it the tab says only whether billing is held.

interface Answer {
  week: string | null;
  seesMoney: boolean;
  visits: {
    id: string;
    date: string;
    service: string;
    caregiver: string;
    units: string;
    billing: { text: string; level: string };
    bill: string;
    pay: string | null;
  }[];
  invoices: { id: string; number: string; period: string; amount: string; status: string }[];
  margin: {
    episode: number;
    billed: string;
    pay: string;
    margin: string;
    pct: number | null;
  } | null;
}

/** Whether this person reaches Home Care › Billing (Billing at View, Home Care on). */
export const seesBilling = (ctx: ScreenContext) =>
  ctx.me.menus.some((m) => m.id === "homecare" && m.screens.some((s) => s.id === "billing"));

export async function patientBillingTab(host: HTMLElement, ctx: ScreenContext, id: string) {
  const res = await api<Answer>("GET", `/api/patients/${encodeURIComponent(id)}/billing`);
  if (!res.ok) {
    host.replaceChildren(el("p", { class: "note", role: "alert" }, "Billing couldn't be loaded."));
    return;
  }
  const a = res.body;
  host.replaceChildren(
    // Brief 1E-33 (mockup v87): the worked visits list takes the place of Visits to bill.
    workedVisitsCard(ctx, "patient", id),
    el(
      "div",
      { class: "grid2" },
      card(
        "Invoices for this patient",
        null,
        [],
        a.invoices.length
          ? table(
              [
                { h: "Invoice", f: (i) => el("b", {}, i.number) },
                { h: "Period", f: (i) => i.period },
                { h: "Amount", num: true, f: (i) => i.amount },
                { h: "Status", f: (i) => chip(i.status, i.status === "Void" ? "bad" : "ok") },
              ],
              a.invoices,
              (i) => ctx.go(`/homecare/billing/${i.id}`),
              "Invoices",
            )
          : el("p", { class: "muted small" }, "None yet."),
      ),
      ...(a.margin
        ? [
            card(
              `Margin · episode ${a.margin.episode} to date`,
              "Billed minus caregiver pay. Reimbursements join with payroll.",
              [],
              el(
                "dl",
                { class: "kv" },
                el("dt", {}, "Billed"),
                el("dd", {}, a.margin.billed),
                el("dt", {}, "Caregiver pay"),
                el("dd", {}, a.margin.pay),
                el("dt", {}, "Margin"),
                el(
                  "dd",
                  {},
                  el("b", {}, a.margin.margin),
                  a.margin.pct !== null ? ` · ${a.margin.pct}%` : "",
                ),
              ),
            ),
          ]
        : []),
    ),
  );
}
